import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixSiteActionsV1SiteActions from './site-actions-v1-site-actions-site-actions.http.js';

/**
 * A package is group of instances of Wix services that a reseller offers to a
 * customer as part of a single transaction.
 */
export interface EmptyEntity {}

export interface PublishSiteRequest {}

export interface PublishSiteResponse {}

export interface DeployStandalonePagesRequest {}

export interface DeployStandalonePagesResponse {}

export interface DuplicateSiteRequest {
  /**
   * ID of the site to duplicated.
   * @format GUID
   */
  sourceSiteId: string;
  /**
   * Display name for the new site.
   * @minLength 1
   * @maxLength 40
   */
  siteDisplayName: string;
}

export interface DuplicateSiteResponse {
  /**
   * ID of the new site.
   * @format GUID
   */
  newSiteId?: string;
}

export interface BulkDeleteSiteRequest {
  /**
   * Site IDs.
   *
   * Min: 1 site ID <br>
   * Max: 20 site IDs
   * @format GUID
   * @minSize 1
   * @maxSize 20
   */
  ids: string[];
}

export interface BulkDeleteSiteResponse {
  /** List of deletion results. */
  results?: BulkSiteResult[];
  /** Summary of deletion data. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkSiteResult {
  /** Result of deletion request per site describing the success or failure of each deletion. */
  itemMetadata?: ItemMetadata;
}

export interface ItemMetadata {
  /** Site ID. */
  _id?: string | null;
  /** Index of the site in the request array. Allows for correlation between request and response. */
  originalIndex?: number;
  /** Whether the requested action was successful for the site. When `false`, the action failed and an `error` object is populated. */
  success?: boolean;
  /** Details about the error. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of error. */
  description?: string;
}

export interface BulkActionMetadata {
  /** Number of sites that were deleted successfully. */
  totalSuccesses?: number;
  /** Number of sites that were not deleted successfully. */
  totalFailures?: number;
}

export interface RenameSiteRequest {
  /**
   * @minLength 1
   * @maxLength 40
   */
  siteNewName?: string;
}

export interface RenameSiteResponse {}

export interface ValidateSiteNameRequest {
  /**
   * @minLength 1
   * @maxLength 40
   */
  siteName?: string;
}

export interface ValidateSiteNameResponse {
  /** Indicates whether the site name is valid. */
  valid?: boolean;
}

export interface UnpublishSiteRequest {}

export interface UnpublishSiteResponse {}

export interface MoveSiteToTrashRequest {}

export interface MoveSiteToTrashResponse {}

export interface RestoreSiteFromTrashRequest {
  /** @format GUID */
  siteId?: string;
}

export interface RestoreSiteFromTrashResponse {}

export interface DeleteSiteFromTrashRequest {
  /** @format GUID */
  siteId?: string;
}

export interface DeleteSiteFromTrashResponse {}

export interface CreateAndAttachChildRequest {
  /** @format GUID */
  templateId?: string;
  /**
   * @minLength 1
   * @maxLength 40
   */
  name?: string;
}

export interface CreateAndAttachChildResponse {
  /** @format GUID */
  childMetaSiteId?: string;
}

/** @docsIgnore */
export type PublishSiteApplicationErrors = {
  code?: 'SITE_IS_NOT_DEFINED_AS_PUBLISHABLE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type DeployStandalonePagesApplicationErrors = {
  code?: 'SITE_IS_NOT_HEADLESS';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Publishes a site.
 *
 * Publishing a site makes any changes previously saved on the site available on the internet.
 * After publishing, changes to your site appear in the [site's history](https://support.wix.com/en/article/viewing-and-managing-your-site-history).
 *
 * When using an API key for authentication, include a `wix-site-id` header with the ID of the site you want to publish.
 * @public
 * @permissionId METASITE.UPDATE
 * @applicableIdentity APP
 * @fqn wix.site.v1.SitePublisher.PublishSite
 */
export async function publishSite(): Promise<
  void & {
    __applicationErrorsType?: PublishSiteApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixSiteActionsV1SiteActions.publishSite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Publishes a headless site.
 *
 * Publishing only the headless pages of the site.
 *
 * When using an API key for authentication, include a `wix-site-id` header with the ID of the site you want to publish.
 * @public
 * @permissionId METASITE.UPDATE
 * @applicableIdentity APP
 * @fqn wix.site.v1.SitePublisher.DeployStandalonePages
 */
export async function deployStandalonePages(): Promise<
  void & {
    __applicationErrorsType?: DeployStandalonePagesApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixSiteActionsV1SiteActions.deployStandalonePages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Duplicates a site with a new site name.
 *
 * > **Note:** When you duplicate a site, some business-related content such as store orders, contacts, invoices, and 3rd-party app settings are not be included.
 * > The duplicated site won’t have a domain or any Premium capabilities.
 *
 * Any installed apps that can be used only on sites with a Premium Plan, will be copied to the duplicated site and
 * will appear unactivated. Once the site is upgraded, the app will be available for use.
 *
 * > **Important:** This call requires an account level API key.
 * @param sourceSiteId - ID of the site to duplicated.
 * @public
 * @requiredField options
 * @requiredField options.siteDisplayName
 * @requiredField sourceSiteId
 * @permissionId my-account.duplicate-site
 * @fqn com.wix.site.actions.SiteActionsService.DuplicateSite
 */
export async function duplicateSite(
  sourceSiteId: string,
  options: NonNullablePaths<DuplicateSiteOptions, `siteDisplayName`, 0>
): Promise<NonNullablePaths<DuplicateSiteResponse, `newSiteId`, 0>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    sourceSiteId: sourceSiteId,
    siteDisplayName: options?.siteDisplayName,
  });

  const reqOpts = ambassadorWixSiteActionsV1SiteActions.duplicateSite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          sourceSiteId: '$[0]',
          siteDisplayName: '$[1].siteDisplayName',
        },
        singleArgumentUnchanged: false,
      },
      ['sourceSiteId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DuplicateSiteOptions {
  /**
   * Display name for the new site.
   * @minLength 1
   * @maxLength 40
   */
  siteDisplayName: string;
}

/**
 * This endpoint enables you to delete multiple sites.
 *
 * This is not a permanent delete. Sites are moved to the trash bin and can be restored through site collaborators.
 *
 * Learn more about [deleting multiple sites](https://support.wix.com/en/article/moving-a-site-to-trash).
 *
 * > **Important:** This call requires an account level API key.
 * @param ids - Site IDs.
 *
 * Min: 1 site ID <br>
 * Max: 20 site IDs
 * @public
 * @requiredField ids
 * @permissionId my-account.delete-site
 * @fqn com.wix.site.actions.SiteActionsService.BulkDeleteSite
 */
export async function bulkDeleteSite(
  ids: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteSiteResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts = ambassadorWixSiteActionsV1SiteActions.bulkDeleteSite(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
